import { Injectable } from '@angular/core';
import { CountryModel } from '../../models/countries/country.model';
import { ECountryRegion } from '../../enums/countryRegion.enum';

@Injectable({ providedIn: 'root' })
export class CountryService {
  countryInfo: {
    name: string;
    alpha2Code: string;
    region: ECountryRegion;
    translations: any;
    callingCodes: string[];
  }[] = [];

  constructor() {
    this.initCountryInfo();
  }

  /**
   * Retrieves a list of countries with their respective codes, names, and regions.
   * The list is sorted alphabetically by country name.
   *
   * @returns {CountryModel[]} - An array of CountryModel objects, each representing a country.
   */
  public getCountries(): CountryModel[] {
    return this.countryInfo
      .map((country) => {
        return {
          code: country.alpha2Code,
          name: country.name,
          region: country.region,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private initCountryInfo(): void {
    this.countryInfo = [
      {
        name: 'Afghanistan',
        alpha2Code: 'AF',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Afghanistan',
          es: 'Afganistán',
          fr: 'Afghanistan',
          jp: 'アフガニスタン',
          it: 'Afghanistan',
        },
        callingCodes: ['93'],
      },
      {
        name: 'Åland Islands',
        alpha2Code: 'AX',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Åland',
          es: 'Alandia',
          fr: 'Åland',
          jp: 'オーランド諸島',
          it: 'Isole Aland',
        },
        callingCodes: ['358'],
      },
      {
        name: 'Albania',
        alpha2Code: 'AL',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Albanien',
          es: 'Albania',
          fr: 'Albanie',
          jp: 'アルバニア',
          it: 'Albania',
        },
        callingCodes: ['355'],
      },
      {
        name: 'Algeria',
        alpha2Code: 'DZ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Algerien',
          es: 'Argelia',
          fr: 'Algérie',
          jp: 'アルジェリア',
          it: 'Algeria',
        },
        callingCodes: ['213'],
      },
      {
        name: 'American Samoa',
        alpha2Code: 'AS',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Amerikanisch-Samoa',
          es: 'Samoa Americana',
          fr: 'Samoa américaines',
          jp: 'アメリカ領サモア',
          it: 'Samoa Americane',
        },
        callingCodes: ['1684'],
      },
      {
        name: 'Andorra',
        alpha2Code: 'AD',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Andorra',
          es: 'Andorra',
          fr: 'Andorre',
          jp: 'アンドラ',
          it: 'Andorra',
        },
        callingCodes: ['376'],
      },
      {
        name: 'Angola',
        alpha2Code: 'AO',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Angola',
          es: 'Angola',
          fr: 'Angola',
          jp: 'アンゴラ',
          it: 'Angola',
        },
        callingCodes: ['244'],
      },
      {
        name: 'Anguilla',
        alpha2Code: 'AI',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Anguilla',
          es: 'Anguilla',
          fr: 'Anguilla',
          jp: 'アンギラ',
          it: 'Anguilla',
        },
        callingCodes: ['1264'],
      },
      {
        name: 'Antarctica',
        alpha2Code: 'AQ',
        region: ECountryRegion.ANTARCTICA,
        translations: {
          de: 'Antarktika',
          es: 'Antártida',
          fr: 'Antarctique',
          jp: '南極大陸',
          it: 'Antartide',
        },
        callingCodes: [],
      },
      {
        name: 'Antigua and Barbuda',
        alpha2Code: 'AG',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Antigua und Barbuda',
          es: 'Antigua y Barbuda',
          fr: 'Antigua-et-Barbuda',
          jp: 'アンティグア・バーブーダ',
          it: 'Antigua e Barbuda',
        },
        callingCodes: ['1268'],
      },
      {
        name: 'Argentina',
        alpha2Code: 'AR',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Argentinien',
          es: 'Argentina',
          fr: 'Argentine',
          jp: 'アルゼンチン',
          it: 'Argentina',
        },
        callingCodes: ['54'],
      },
      {
        name: 'Armenia',
        alpha2Code: 'AM',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Armenien',
          es: 'Armenia',
          fr: 'Arménie',
          jp: 'アルメニア',
          it: 'Armenia',
        },
        callingCodes: ['374'],
      },
      {
        name: 'Aruba',
        alpha2Code: 'AW',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Aruba',
          es: 'Aruba',
          fr: 'Aruba',
          jp: 'アルバ',
          it: 'Aruba',
        },
        callingCodes: ['297'],
      },
      {
        name: 'Australia',
        alpha2Code: 'AU',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Australien',
          es: 'Australia',
          fr: 'Australie',
          jp: 'オーストラリア',
          it: 'Australia',
        },
        callingCodes: ['61'],
      },
      {
        name: 'Austria',
        alpha2Code: 'AT',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Österreich',
          es: 'Austria',
          fr: 'Autriche',
          jp: 'オーストリア',
          it: 'Austria',
        },
        callingCodes: ['43'],
      },
      {
        name: 'Azerbaijan',
        alpha2Code: 'AZ',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Aserbaidschan',
          es: 'Azerbaiyán',
          fr: 'Azerbaïdjan',
          jp: 'アゼルバイジャン',
          it: 'Azerbaijan',
        },
        callingCodes: ['994'],
      },
      {
        name: 'The Bahamas',
        alpha2Code: 'BS',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Bahamas',
          es: 'Bahamas',
          fr: 'Bahamas',
          jp: 'バハマ',
          it: 'Bahamas',
        },
        callingCodes: ['1242'],
      },
      {
        name: 'Bahrain',
        alpha2Code: 'BH',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Bahrain',
          es: 'Bahrein',
          fr: 'Bahreïn',
          jp: 'バーレーン',
          it: 'Bahrein',
        },
        callingCodes: ['973'],
      },
      {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Bangladesch',
          es: 'Bangladesh',
          fr: 'Bangladesh',
          jp: 'バングラデシュ',
          it: 'Bangladesh',
        },
        callingCodes: ['880'],
      },
      {
        name: 'Barbados',
        alpha2Code: 'BB',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Barbados',
          es: 'Barbados',
          fr: 'Barbade',
          jp: 'バルバドス',
          it: 'Barbados',
        },
        callingCodes: ['1246'],
      },
      {
        name: 'Belarus',
        alpha2Code: 'BY',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Weißrussland',
          es: 'Bielorrusia',
          fr: 'Biélorussie',
          jp: 'ベラルーシ',
          it: 'Bielorussia',
        },
        callingCodes: ['375'],
      },
      {
        name: 'Belgium',
        alpha2Code: 'BE',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Belgien',
          es: 'Bélgica',
          fr: 'Belgique',
          jp: 'ベルギー',
          it: 'Belgio',
        },
        callingCodes: ['32'],
      },
      {
        name: 'Belize',
        alpha2Code: 'BZ',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Belize',
          es: 'Belice',
          fr: 'Belize',
          jp: 'ベリーズ',
          it: 'Belize',
        },
        callingCodes: ['501'],
      },
      {
        name: 'Benin',
        alpha2Code: 'BJ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Benin',
          es: 'Benín',
          fr: 'Bénin',
          jp: 'ベナン',
          it: 'Benin',
        },
        callingCodes: ['229'],
      },
      {
        name: 'Bermuda',
        alpha2Code: 'BM',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Bermuda',
          es: 'Bermudas',
          fr: 'Bermudes',
          jp: 'バミューダ',
          it: 'Bermuda',
        },
        callingCodes: ['1441'],
      },
      {
        name: 'Bhutan',
        alpha2Code: 'BT',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Bhutan',
          es: 'Bután',
          fr: 'Bhoutan',
          jp: 'ブータン',
          it: 'Bhutan',
        },
        callingCodes: ['975'],
      },
      {
        name: 'Bolivia',
        alpha2Code: 'BO',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Bolivien',
          es: 'Bolivia',
          fr: 'Bolivie',
          jp: 'ボリビア多民族国',
          it: 'Bolivia',
        },
        callingCodes: ['591'],
      },
      {
        name: 'Bonaire',
        alpha2Code: 'BQ',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: null,
          es: null,
          fr: null,
          jp: null,
          it: null,
        },
        callingCodes: ['5997'],
      },
      {
        name: 'Bosnia and Herzegovina',
        alpha2Code: 'BA',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Bosnien und Herzegowina',
          es: 'Bosnia y Herzegovina',
          fr: 'Bosnie-Herzégovine',
          jp: 'ボスニア・ヘルツェゴビナ',
          it: 'Bosnia ed Erzegovina',
        },
        callingCodes: ['387'],
      },
      {
        name: 'Botswana',
        alpha2Code: 'BW',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Botswana',
          es: 'Botswana',
          fr: 'Botswana',
          jp: 'ボツワナ',
          it: 'Botswana',
        },
        callingCodes: ['267'],
      },
      {
        name: 'Bouvet Island',
        alpha2Code: 'BV',
        region: ECountryRegion.NONE,
        translations: {
          de: 'Bouvetinsel',
          es: 'Isla Bouvet',
          fr: 'Île Bouvet',
          jp: 'ブーベ島',
          it: 'Isola Bouvet',
        },
        callingCodes: [''],
      },
      {
        name: 'Brazil',
        alpha2Code: 'BR',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Brasilien',
          es: 'Brasil',
          fr: 'Brésil',
          jp: 'ブラジル',
          it: 'Brasile',
        },
        callingCodes: ['55'],
      },
      {
        name: 'British Indian Ocean Territory',
        alpha2Code: 'IO',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Britisches Territorium im Indischen Ozean',
          es: 'Territorio Británico del Océano Índico',
          fr: "Territoire britannique de l'océan Indien",
          jp: 'イギリス領インド洋地域',
          it: "Territorio britannico dell'oceano indiano",
        },
        callingCodes: ['246'],
      },
      {
        name: 'United States Minor Outlying Islands',
        alpha2Code: 'UM',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Kleinere Inselbesitzungen der Vereinigten Staaten',
          es: 'Islas Ultramarinas Menores de Estados Unidos',
          fr: 'Îles mineures éloignées des États-Unis',
          jp: '合衆国領有小離島',
          it: "Isole minori esterne degli Stati Uniti d'America",
        },
        callingCodes: [''],
      },
      {
        name: 'Virgin Islands (British)',
        alpha2Code: 'VG',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Britische Jungferninseln',
          es: 'Islas Vírgenes del Reino Unido',
          fr: 'Îles Vierges britanniques',
          jp: 'イギリス領ヴァージン諸島',
          it: 'Isole Vergini Britanniche',
        },
        callingCodes: ['1284'],
      },
      {
        name: 'Virgin Islands (U.S.)',
        alpha2Code: 'VI',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Amerikanische Jungferninseln',
          es: 'Islas Vírgenes de los Estados Unidos',
          fr: 'Îles Vierges des États-Unis',
          jp: 'アメリカ領ヴァージン諸島',
          it: 'Isole Vergini americane',
        },
        callingCodes: ['1 340'],
      },
      {
        name: 'Brunei',
        alpha2Code: 'BN',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Brunei',
          es: 'Brunei',
          fr: 'Brunei',
          jp: 'ブルネイ・ダルサラーム',
          it: 'Brunei',
        },
        callingCodes: ['673'],
      },
      {
        name: 'Bulgaria',
        alpha2Code: 'BG',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Bulgarien',
          es: 'Bulgaria',
          fr: 'Bulgarie',
          jp: 'ブルガリア',
          it: 'Bulgaria',
        },
        callingCodes: ['359'],
      },
      {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Burkina Faso',
          es: 'Burkina Faso',
          fr: 'Burkina Faso',
          jp: 'ブルキナファソ',
          it: 'Burkina Faso',
        },
        callingCodes: ['226'],
      },
      {
        name: 'Burundi',
        alpha2Code: 'BI',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Burundi',
          es: 'Burundi',
          fr: 'Burundi',
          jp: 'ブルンジ',
          it: 'Burundi',
        },
        callingCodes: ['257'],
      },
      {
        name: 'Cambodia',
        alpha2Code: 'KH',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Kambodscha',
          es: 'Camboya',
          fr: 'Cambodge',
          jp: 'カンボジア',
          it: 'Cambogia',
        },
        callingCodes: ['855'],
      },
      {
        name: 'Cameroon',
        alpha2Code: 'CM',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Kamerun',
          es: 'Camerún',
          fr: 'Cameroun',
          jp: 'カメルーン',
          it: 'Camerun',
        },
        callingCodes: ['237'],
      },
      {
        name: 'Canada',
        alpha2Code: 'CA',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Kanada',
          es: 'Canadá',
          fr: 'Canada',
          jp: 'カナダ',
          it: 'Canada',
        },
        callingCodes: ['1'],
      },
      {
        name: 'Cape Verde',
        alpha2Code: 'CV',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Kap Verde',
          es: 'Cabo Verde',
          fr: 'Cap Vert',
          jp: 'カーボベルデ',
          it: 'Capo Verde',
        },
        callingCodes: ['238'],
      },
      {
        name: 'Cayman Islands',
        alpha2Code: 'KY',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Kaimaninseln',
          es: 'Islas Caimán',
          fr: 'Îles Caïmans',
          jp: 'ケイマン諸島',
          it: 'Isole Cayman',
        },
        callingCodes: ['1345'],
      },
      {
        name: 'Central African Republic',
        alpha2Code: 'CF',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Zentralafrikanische Republik',
          es: 'República Centroafricana',
          fr: 'République centrafricaine',
          jp: '中央アフリカ共和国',
          it: 'Repubblica Centrafricana',
        },
        callingCodes: ['236'],
      },
      {
        name: 'Chad',
        alpha2Code: 'TD',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Tschad',
          es: 'Chad',
          fr: 'Tchad',
          jp: 'チャド',
          it: 'Ciad',
        },
        callingCodes: ['235'],
      },
      {
        name: 'Chile',
        alpha2Code: 'CL',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Chile',
          es: 'Chile',
          fr: 'Chili',
          jp: 'チリ',
          it: 'Cile',
        },
        callingCodes: ['56'],
      },
      {
        name: 'China',
        alpha2Code: 'CN',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'China',
          es: 'China',
          fr: 'Chine',
          jp: '中国',
          it: 'Cina',
        },
        callingCodes: ['86'],
      },
      {
        name: 'Christmas Island',
        alpha2Code: 'CX',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Weihnachtsinsel',
          es: 'Isla de Navidad',
          fr: 'Île Christmas',
          jp: 'クリスマス島',
          it: 'Isola di Natale',
        },
        callingCodes: ['61'],
      },
      {
        name: 'Cocos (Keeling) Islands',
        alpha2Code: 'CC',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Kokosinseln',
          es: 'Islas Cocos o Islas Keeling',
          fr: 'Îles Cocos',
          jp: 'ココス（キーリング）諸島',
          it: 'Isole Cocos e Keeling',
        },
        callingCodes: ['61'],
      },
      {
        name: 'Colombia',
        alpha2Code: 'CO',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Kolumbien',
          es: 'Colombia',
          fr: 'Colombie',
          jp: 'コロンビア',
          it: 'Colombia',
        },
        callingCodes: ['57'],
      },
      {
        name: 'Comoros',
        alpha2Code: 'KM',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Union der Komoren',
          es: 'Comoras',
          fr: 'Comores',
          jp: 'コモロ',
          it: 'Comore',
        },
        callingCodes: ['269'],
      },
      {
        name: 'Republic of the Congo',
        alpha2Code: 'CG',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Kongo',
          es: 'Congo',
          fr: 'Congo',
          jp: 'コンゴ共和国',
          it: 'Congo',
        },
        callingCodes: ['242'],
      },
      {
        name: 'Democratic Republic of the Congo',
        alpha2Code: 'CD',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Kongo (Dem. Rep.)',
          es: 'Congo (Rep. Dem.)',
          fr: 'Congo (Rép. dém.)',
          jp: 'コンゴ民主共和国',
          it: 'Congo (Rep. Dem.)',
        },
        callingCodes: ['243'],
      },
      {
        name: 'Cook Islands',
        alpha2Code: 'CK',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Cookinseln',
          es: 'Islas Cook',
          fr: 'Îles Cook',
          jp: 'クック諸島',
          it: 'Isole Cook',
        },
        callingCodes: ['682'],
      },
      {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Costa Rica',
          es: 'Costa Rica',
          fr: 'Costa Rica',
          jp: 'コスタリカ',
          it: 'Costa Rica',
        },
        callingCodes: ['506'],
      },
      {
        name: 'Croatia',
        alpha2Code: 'HR',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Kroatien',
          es: 'Croacia',
          fr: 'Croatie',
          jp: 'クロアチア',
          it: 'Croazia',
        },
        callingCodes: ['385'],
      },
      {
        name: 'Cuba',
        alpha2Code: 'CU',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Kuba',
          es: 'Cuba',
          fr: 'Cuba',
          jp: 'キューバ',
          it: 'Cuba',
        },
        callingCodes: ['53'],
      },
      {
        name: 'Curaçao',
        alpha2Code: 'CW',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: null,
          es: null,
          fr: null,
          jp: null,
          it: null,
        },
        callingCodes: ['599'],
      },
      {
        name: 'Cyprus',
        alpha2Code: 'CY',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Zypern',
          es: 'Chipre',
          fr: 'Chypre',
          jp: 'キプロス',
          it: 'Cipro',
        },
        callingCodes: ['357'],
      },
      {
        name: 'Czech Republic',
        alpha2Code: 'CZ',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Tschechische Republik',
          es: 'República Checa',
          fr: 'République tchèque',
          jp: 'チェコ',
          it: 'Repubblica Ceca',
        },
        callingCodes: ['420'],
      },
      {
        name: 'Denmark',
        alpha2Code: 'DK',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Dänemark',
          es: 'Dinamarca',
          fr: 'Danemark',
          jp: 'デンマーク',
          it: 'Danimarca',
        },
        callingCodes: ['45'],
      },
      {
        name: 'Djibouti',
        alpha2Code: 'DJ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Dschibuti',
          es: 'Yibuti',
          fr: 'Djibouti',
          jp: 'ジブチ',
          it: 'Gibuti',
        },
        callingCodes: ['253'],
      },
      {
        name: 'Dominica',
        alpha2Code: 'DM',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Dominica',
          es: 'Dominica',
          fr: 'Dominique',
          jp: 'ドミニカ国',
          it: 'Dominica',
        },
        callingCodes: ['1767'],
      },
      {
        name: 'Dominican Republic',
        alpha2Code: 'DO',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Dominikanische Republik',
          es: 'República Dominicana',
          fr: 'République dominicaine',
          jp: 'ドミニカ共和国',
          it: 'Repubblica Dominicana',
        },
        callingCodes: ['1809', '1829', '1849'],
      },
      {
        name: 'Ecuador',
        alpha2Code: 'EC',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Ecuador',
          es: 'Ecuador',
          fr: 'Équateur',
          jp: 'エクアドル',
          it: 'Ecuador',
        },
        callingCodes: ['593'],
      },
      {
        name: 'Egypt',
        alpha2Code: 'EG',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Ägypten',
          es: 'Egipto',
          fr: 'Égypte',
          jp: 'エジプト',
          it: 'Egitto',
        },
        callingCodes: ['20'],
      },
      {
        name: 'El Salvador',
        alpha2Code: 'SV',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'El Salvador',
          es: 'El Salvador',
          fr: 'Salvador',
          jp: 'エルサルバドル',
          it: 'El Salvador',
        },
        callingCodes: ['503'],
      },
      {
        name: 'Equatorial Guinea',
        alpha2Code: 'GQ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Äquatorial-Guinea',
          es: 'Guinea Ecuatorial',
          fr: 'Guinée-Équatoriale',
          jp: '赤道ギニア',
          it: 'Guinea Equatoriale',
        },
        callingCodes: ['240'],
      },
      {
        name: 'Eritrea',
        alpha2Code: 'ER',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Eritrea',
          es: 'Eritrea',
          fr: 'Érythrée',
          jp: 'エリトリア',
          it: 'Eritrea',
        },
        callingCodes: ['291'],
      },
      {
        name: 'Estonia',
        alpha2Code: 'EE',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Estland',
          es: 'Estonia',
          fr: 'Estonie',
          jp: 'エストニア',
          it: 'Estonia',
        },
        callingCodes: ['372'],
      },
      {
        name: 'Ethiopia',
        alpha2Code: 'ET',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Äthiopien',
          es: 'Etiopía',
          fr: 'Éthiopie',
          jp: 'エチオピア',
          it: 'Etiopia',
        },
        callingCodes: ['251'],
      },
      {
        name: 'Falkland Islands',
        alpha2Code: 'FK',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Falklandinseln',
          es: 'Islas Malvinas',
          fr: 'Îles Malouines',
          jp: 'フォークランド（マルビナス）諸島',
          it: 'Isole Falkland o Isole Malvine',
        },
        callingCodes: ['500'],
      },
      {
        name: 'Faroe Islands',
        alpha2Code: 'FO',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Färöer-Inseln',
          es: 'Islas Faroe',
          fr: 'Îles Féroé',
          jp: 'フェロー諸島',
          it: 'Isole Far Oer',
        },
        callingCodes: ['298'],
      },
      {
        name: 'Fiji',
        alpha2Code: 'FJ',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Fidschi',
          es: 'Fiyi',
          fr: 'Fidji',
          jp: 'フィジー',
          it: 'Figi',
        },
        callingCodes: ['679'],
      },
      {
        name: 'Finland',
        alpha2Code: 'FI',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Finnland',
          es: 'Finlandia',
          fr: 'Finlande',
          jp: 'フィンランド',
          it: 'Finlandia',
        },
        callingCodes: ['358'],
      },
      {
        name: 'France',
        alpha2Code: 'FR',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Frankreich',
          es: 'Francia',
          fr: 'France',
          jp: 'フランス',
          it: 'Francia',
        },
        callingCodes: ['33'],
      },
      {
        name: 'French Guiana',
        alpha2Code: 'GF',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Französisch Guyana',
          es: 'Guayana Francesa',
          fr: 'Guayane',
          jp: 'フランス領ギアナ',
          it: 'Guyana francese',
        },
        callingCodes: ['594'],
      },
      {
        name: 'French Polynesia',
        alpha2Code: 'PF',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Französisch-Polynesien',
          es: 'Polinesia Francesa',
          fr: 'Polynésie française',
          jp: 'フランス領ポリネシア',
          it: 'Polinesia Francese',
        },
        callingCodes: ['689'],
      },
      {
        name: 'French Southern and Antarctic Lands',
        alpha2Code: 'TF',
        region: ECountryRegion.NONE,
        translations: {
          de: 'Französische Süd- und Antarktisgebiete',
          es: 'Tierras Australes y Antárticas Francesas',
          fr: 'Terres australes et antarctiques françaises',
          jp: 'フランス領南方・南極地域',
          it: 'Territori Francesi del Sud',
        },
        callingCodes: [''],
      },
      {
        name: 'Gabon',
        alpha2Code: 'GA',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Gabun',
          es: 'Gabón',
          fr: 'Gabon',
          jp: 'ガボン',
          it: 'Gabon',
        },
        callingCodes: ['241'],
      },
      {
        name: 'The Gambia',
        alpha2Code: 'GM',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Gambia',
          es: 'Gambia',
          fr: 'Gambie',
          jp: 'ガンビア',
          it: 'Gambia',
        },
        callingCodes: ['220'],
      },
      {
        name: 'Georgia',
        alpha2Code: 'GE',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Georgien',
          es: 'Georgia',
          fr: 'Géorgie',
          jp: 'グルジア',
          it: 'Georgia',
        },
        callingCodes: ['995'],
      },
      {
        name: 'Germany',
        alpha2Code: 'DE',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Deutschland',
          es: 'Alemania',
          fr: 'Allemagne',
          jp: 'ドイツ',
          it: 'Germania',
        },
        callingCodes: ['49'],
      },
      {
        name: 'Ghana',
        alpha2Code: 'GH',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Ghana',
          es: 'Ghana',
          fr: 'Ghana',
          jp: 'ガーナ',
          it: 'Ghana',
        },
        callingCodes: ['233'],
      },
      {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Gibraltar',
          es: 'Gibraltar',
          fr: 'Gibraltar',
          jp: 'ジブラルタル',
          it: 'Gibilterra',
        },
        callingCodes: ['350'],
      },
      {
        name: 'Greece',
        alpha2Code: 'GR',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Griechenland',
          es: 'Grecia',
          fr: 'Grèce',
          jp: 'ギリシャ',
          it: 'Grecia',
        },
        callingCodes: ['30'],
      },
      {
        name: 'Greenland',
        alpha2Code: 'GL',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Grönland',
          es: 'Groenlandia',
          fr: 'Groenland',
          jp: 'グリーンランド',
          it: 'Groenlandia',
        },
        callingCodes: ['299'],
      },
      {
        name: 'Grenada',
        alpha2Code: 'GD',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Grenada',
          es: 'Grenada',
          fr: 'Grenade',
          jp: 'グレナダ',
          it: 'Grenada',
        },
        callingCodes: ['1473'],
      },
      {
        name: 'Guadeloupe',
        alpha2Code: 'GP',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Guadeloupe',
          es: 'Guadalupe',
          fr: 'Guadeloupe',
          jp: 'グアドループ',
          it: 'Guadeloupa',
        },
        callingCodes: ['590'],
      },
      {
        name: 'Guam',
        alpha2Code: 'GU',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Guam',
          es: 'Guam',
          fr: 'Guam',
          jp: 'グアム',
          it: 'Guam',
        },
        callingCodes: ['1671'],
      },
      {
        name: 'Guatemala',
        alpha2Code: 'GT',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Guatemala',
          es: 'Guatemala',
          fr: 'Guatemala',
          jp: 'グアテマラ',
          it: 'Guatemala',
        },
        callingCodes: ['502'],
      },
      {
        name: 'Guernsey',
        alpha2Code: 'GG',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Guernsey',
          es: 'Guernsey',
          fr: 'Guernesey',
          jp: 'ガーンジー',
          it: 'Guernsey',
        },
        callingCodes: ['44'],
      },
      {
        name: 'Guinea',
        alpha2Code: 'GN',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Guinea',
          es: 'Guinea',
          fr: 'Guinée',
          jp: 'ギニア',
          it: 'Guinea',
        },
        callingCodes: ['224'],
      },
      {
        name: 'Guinea-Bissau',
        alpha2Code: 'GW',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Guinea-Bissau',
          es: 'Guinea-Bisáu',
          fr: 'Guinée-Bissau',
          jp: 'ギニアビサウ',
          it: 'Guinea-Bissau',
        },
        callingCodes: ['245'],
      },
      {
        name: 'Guyana',
        alpha2Code: 'GY',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Guyana',
          es: 'Guyana',
          fr: 'Guyane',
          jp: 'ガイアナ',
          it: 'Guyana',
        },
        callingCodes: ['592'],
      },
      {
        name: 'Haiti',
        alpha2Code: 'HT',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Haiti',
          es: 'Haiti',
          fr: 'Haïti',
          jp: 'ハイチ',
          it: 'Haiti',
        },
        callingCodes: ['509'],
      },
      {
        name: 'Heard Island and McDonald Islands',
        alpha2Code: 'HM',
        region: ECountryRegion.NONE,
        translations: {
          de: 'Heard und die McDonaldinseln',
          es: 'Islas Heard y McDonald',
          fr: 'Îles Heard-et-MacDonald',
          jp: 'ハード島とマクドナルド諸島',
          it: 'Isole Heard e McDonald',
        },
        callingCodes: [''],
      },
      {
        name: 'Holy See',
        alpha2Code: 'VA',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Heiliger Stuhl',
          es: 'Santa Sede',
          fr: 'voir Saint',
          jp: '聖座',
          it: 'Santa Sede',
        },
        callingCodes: ['379'],
      },
      {
        name: 'Honduras',
        alpha2Code: 'HN',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Honduras',
          es: 'Honduras',
          fr: 'Honduras',
          jp: 'ホンジュラス',
          it: 'Honduras',
        },
        callingCodes: ['504'],
      },
      {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Hong Kong',
          es: 'Hong Kong',
          fr: 'Hong Kong',
          jp: '香港',
          it: 'Hong Kong',
        },
        callingCodes: ['852'],
      },
      {
        name: 'Hungary',
        alpha2Code: 'HU',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Ungarn',
          es: 'Hungría',
          fr: 'Hongrie',
          jp: 'ハンガリー',
          it: 'Ungheria',
        },
        callingCodes: ['36'],
      },
      {
        name: 'Iceland',
        alpha2Code: 'IS',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Island',
          es: 'Islandia',
          fr: 'Islande',
          jp: 'アイスランド',
          it: 'Islanda',
        },
        callingCodes: ['354'],
      },
      {
        name: 'India',
        alpha2Code: 'IN',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Indien',
          es: 'India',
          fr: 'Inde',
          jp: 'インド',
          it: 'India',
        },
        callingCodes: ['91'],
      },
      {
        name: 'Indonesia',
        alpha2Code: 'ID',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Indonesien',
          es: 'Indonesia',
          fr: 'Indonésie',
          jp: 'インドネシア',
          it: 'Indonesia',
        },
        callingCodes: ['62'],
      },
      {
        name: 'Ivory Coast',
        alpha2Code: 'CI',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Elfenbeinküste',
          es: 'Costa de Marfil',
          fr: "Côte d'Ivoire",
          jp: 'コートジボワール',
          it: "Costa D'Avorio",
        },
        callingCodes: ['225'],
      },
      {
        name: 'Iran',
        alpha2Code: 'IR',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Iran',
          es: 'Iran',
          fr: 'Iran',
          jp: 'イラン・イスラム共和国',
          it: null,
        },
        callingCodes: ['98'],
      },
      {
        name: 'Iraq',
        alpha2Code: 'IQ',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Irak',
          es: 'Irak',
          fr: 'Irak',
          jp: 'イラク',
          it: 'Iraq',
        },
        callingCodes: ['964'],
      },
      {
        name: 'Republic of Ireland',
        alpha2Code: 'IE',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Irland',
          es: 'Irlanda',
          fr: 'Irlande',
          jp: 'アイルランド',
          it: 'Irlanda',
        },
        callingCodes: ['353'],
      },
      {
        name: 'Isle of Man',
        alpha2Code: 'IM',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Insel Man',
          es: 'Isla de Man',
          fr: 'Île de Man',
          jp: 'マン島',
          it: 'Isola di Man',
        },
        callingCodes: ['44'],
      },
      {
        name: 'Israel',
        alpha2Code: 'IL',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Israel',
          es: 'Israel',
          fr: 'Israël',
          jp: 'イスラエル',
          it: 'Israele',
        },
        callingCodes: ['972'],
      },
      {
        name: 'Italy',
        alpha2Code: 'IT',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Italien',
          es: 'Italia',
          fr: 'Italie',
          jp: 'イタリア',
          it: 'Italia',
        },
        callingCodes: ['39'],
      },
      {
        name: 'Jamaica',
        alpha2Code: 'JM',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Jamaika',
          es: 'Jamaica',
          fr: 'Jamaïque',
          jp: 'ジャマイカ',
          it: 'Giamaica',
        },
        callingCodes: ['1876'],
      },
      {
        name: 'Japan',
        alpha2Code: 'JP',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Japan',
          es: 'Japón',
          fr: 'Japon',
          jp: '日本',
          it: 'Giappone',
        },
        callingCodes: ['81'],
      },
      {
        name: 'Jersey',
        alpha2Code: 'JE',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Jersey',
          es: 'Jersey',
          fr: 'Jersey',
          jp: 'ジャージー',
          it: 'Isola di Jersey',
        },
        callingCodes: ['44'],
      },
      {
        name: 'Jordan',
        alpha2Code: 'JO',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Jordanien',
          es: 'Jordania',
          fr: 'Jordanie',
          jp: 'ヨルダン',
          it: 'Giordania',
        },
        callingCodes: ['962'],
      },
      {
        name: 'Kazakhstan',
        alpha2Code: 'KZ',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Kasachstan',
          es: 'Kazajistán',
          fr: 'Kazakhstan',
          jp: 'カザフスタン',
          it: 'Kazakistan',
        },
        callingCodes: ['76', '77'],
      },
      {
        name: 'Kenya',
        alpha2Code: 'KE',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Kenia',
          es: 'Kenia',
          fr: 'Kenya',
          jp: 'ケニア',
          it: 'Kenya',
        },
        callingCodes: ['254'],
      },
      {
        name: 'Kiribati',
        alpha2Code: 'KI',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Kiribati',
          es: 'Kiribati',
          fr: 'Kiribati',
          jp: 'キリバス',
          it: 'Kiribati',
        },
        callingCodes: ['686'],
      },
      {
        name: 'Kuwait',
        alpha2Code: 'KW',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Kuwait',
          es: 'Kuwait',
          fr: 'Koweït',
          jp: 'クウェート',
          it: 'Kuwait',
        },
        callingCodes: ['965'],
      },
      {
        name: 'Kyrgyzstan',
        alpha2Code: 'KG',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Kirgisistan',
          es: 'Kirguizistán',
          fr: 'Kirghizistan',
          jp: 'キルギス',
          it: 'Kirghizistan',
        },
        callingCodes: ['996'],
      },
      {
        name: 'Laos',
        alpha2Code: 'LA',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Laos',
          es: 'Laos',
          fr: 'Laos',
          jp: 'ラオス人民民主共和国',
          it: 'Laos',
        },
        callingCodes: ['856'],
      },
      {
        name: 'Latvia',
        alpha2Code: 'LV',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Lettland',
          es: 'Letonia',
          fr: 'Lettonie',
          jp: 'ラトビア',
          it: 'Lettonia',
        },
        callingCodes: ['371'],
      },
      {
        name: 'Lebanon',
        alpha2Code: 'LB',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Libanon',
          es: 'Líbano',
          fr: 'Liban',
          jp: 'レバノン',
          it: 'Libano',
        },
        callingCodes: ['961'],
      },
      {
        name: 'Lesotho',
        alpha2Code: 'LS',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Lesotho',
          es: 'Lesotho',
          fr: 'Lesotho',
          jp: 'レソト',
          it: 'Lesotho',
        },
        callingCodes: ['266'],
      },
      {
        name: 'Liberia',
        alpha2Code: 'LR',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Liberia',
          es: 'Liberia',
          fr: 'Liberia',
          jp: 'リベリア',
          it: 'Liberia',
        },
        callingCodes: ['231'],
      },
      {
        name: 'Libya',
        alpha2Code: 'LY',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Libyen',
          es: 'Libia',
          fr: 'Libye',
          jp: 'リビア',
          it: 'Libia',
        },
        callingCodes: ['218'],
      },
      {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Liechtenstein',
          es: 'Liechtenstein',
          fr: 'Liechtenstein',
          jp: 'リヒテンシュタイン',
          it: 'Liechtenstein',
        },
        callingCodes: ['423'],
      },
      {
        name: 'Lithuania',
        alpha2Code: 'LT',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Litauen',
          es: 'Lituania',
          fr: 'Lituanie',
          jp: 'リトアニア',
          it: 'Lituania',
        },
        callingCodes: ['370'],
      },
      {
        name: 'Luxembourg',
        alpha2Code: 'LU',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Luxemburg',
          es: 'Luxemburgo',
          fr: 'Luxembourg',
          jp: 'ルクセンブルク',
          it: 'Lussemburgo',
        },
        callingCodes: ['352'],
      },
      {
        name: 'Macau',
        alpha2Code: 'MO',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Macao',
          es: 'Macao',
          fr: 'Macao',
          jp: 'マカオ',
          it: 'Macao',
        },
        callingCodes: ['853'],
      },
      {
        name: 'Republic of Macedonia',
        alpha2Code: 'MK',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Mazedonien',
          es: 'Macedonia',
          fr: 'Macédoine',
          jp: 'マケドニア旧ユーゴスラビア共和国',
          it: 'Macedonia',
        },
        callingCodes: ['389'],
      },
      {
        name: 'Madagascar',
        alpha2Code: 'MG',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Madagaskar',
          es: 'Madagascar',
          fr: 'Madagascar',
          jp: 'マダガスカル',
          it: 'Madagascar',
        },
        callingCodes: ['261'],
      },
      {
        name: 'Malawi',
        alpha2Code: 'MW',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Malawi',
          es: 'Malawi',
          fr: 'Malawi',
          jp: 'マラウイ',
          it: 'Malawi',
        },
        callingCodes: ['265'],
      },
      {
        name: 'Malaysia',
        alpha2Code: 'MY',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Malaysia',
          es: 'Malasia',
          fr: 'Malaisie',
          jp: 'マレーシア',
          it: 'Malesia',
        },
        callingCodes: ['60'],
      },
      {
        name: 'Maldives',
        alpha2Code: 'MV',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Malediven',
          es: 'Maldivas',
          fr: 'Maldives',
          jp: 'モルディブ',
          it: 'Maldive',
        },
        callingCodes: ['960'],
      },
      {
        name: 'Mali',
        alpha2Code: 'ML',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Mali',
          es: 'Mali',
          fr: 'Mali',
          jp: 'マリ',
          it: 'Mali',
        },
        callingCodes: ['223'],
      },
      {
        name: 'Malta',
        alpha2Code: 'MT',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Malta',
          es: 'Malta',
          fr: 'Malte',
          jp: 'マルタ',
          it: 'Malta',
        },
        callingCodes: ['356'],
      },
      {
        name: 'Marshall Islands',
        alpha2Code: 'MH',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Marshallinseln',
          es: 'Islas Marshall',
          fr: 'Îles Marshall',
          jp: 'マーシャル諸島',
          it: 'Isole Marshall',
        },
        callingCodes: ['692'],
      },
      {
        name: 'Martinique',
        alpha2Code: 'MQ',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Martinique',
          es: 'Martinica',
          fr: 'Martinique',
          jp: 'マルティニーク',
          it: 'Martinica',
        },
        callingCodes: ['596'],
      },
      {
        name: 'Mauritania',
        alpha2Code: 'MR',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Mauretanien',
          es: 'Mauritania',
          fr: 'Mauritanie',
          jp: 'モーリタニア',
          it: 'Mauritania',
        },
        callingCodes: ['222'],
      },
      {
        name: 'Mauritius',
        alpha2Code: 'MU',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Mauritius',
          es: 'Mauricio',
          fr: 'Île Maurice',
          jp: 'モーリシャス',
          it: 'Mauritius',
        },
        callingCodes: ['230'],
      },
      {
        name: 'Mayotte',
        alpha2Code: 'YT',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Mayotte',
          es: 'Mayotte',
          fr: 'Mayotte',
          jp: 'マヨット',
          it: 'Mayotte',
        },
        callingCodes: ['262'],
      },
      {
        name: 'Mexico',
        alpha2Code: 'MX',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Mexiko',
          es: 'México',
          fr: 'Mexique',
          jp: 'メキシコ',
          it: 'Messico',
        },
        callingCodes: ['52'],
      },
      {
        name: 'Federated States of Micronesia',
        alpha2Code: 'FM',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Mikronesien',
          es: 'Micronesia',
          fr: 'Micronésie',
          jp: 'ミクロネシア連邦',
          it: 'Micronesia',
        },
        callingCodes: ['691'],
      },
      {
        name: 'Moldova',
        alpha2Code: 'MD',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Moldawie',
          es: 'Moldavia',
          fr: 'Moldavie',
          jp: 'モルドバ共和国',
          it: 'Moldavia',
        },
        callingCodes: ['373'],
      },
      {
        name: 'Monaco',
        alpha2Code: 'MC',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Monaco',
          es: 'Mónaco',
          fr: 'Monaco',
          jp: 'モナコ',
          it: 'Principato di Monaco',
        },
        callingCodes: ['377'],
      },
      {
        name: 'Mongolia',
        alpha2Code: 'MN',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Mongolei',
          es: 'Mongolia',
          fr: 'Mongolie',
          jp: 'モンゴル',
          it: 'Mongolia',
        },
        callingCodes: ['976'],
      },
      {
        name: 'Montenegro',
        alpha2Code: 'ME',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Montenegro',
          es: 'Montenegro',
          fr: 'Monténégro',
          jp: 'モンテネグロ',
          it: 'Montenegro',
        },
        callingCodes: ['382'],
      },
      {
        name: 'Montserrat',
        alpha2Code: 'MS',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Montserrat',
          es: 'Montserrat',
          fr: 'Montserrat',
          jp: 'モントセラト',
          it: 'Montserrat',
        },
        callingCodes: ['1664'],
      },
      {
        name: 'Morocco',
        alpha2Code: 'MA',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Marokko',
          es: 'Marruecos',
          fr: 'Maroc',
          jp: 'モロッコ',
          it: 'Marocco',
        },
        callingCodes: ['212'],
      },
      {
        name: 'Mozambique',
        alpha2Code: 'MZ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Mosambik',
          es: 'Mozambique',
          fr: 'Mozambique',
          jp: 'モザンビーク',
          it: 'Mozambico',
        },
        callingCodes: ['258'],
      },
      {
        name: 'Myanmar',
        alpha2Code: 'MM',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Myanmar',
          es: 'Myanmar',
          fr: 'Myanmar',
          jp: 'ミャンマー',
          it: 'Birmania',
        },
        callingCodes: ['95'],
      },
      {
        name: 'Namibia',
        alpha2Code: 'NA',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Namibia',
          es: 'Namibia',
          fr: 'Namibie',
          jp: 'ナミビア',
          it: 'Namibia',
        },
        callingCodes: ['264'],
      },
      {
        name: 'Nauru',
        alpha2Code: 'NR',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Nauru',
          es: 'Nauru',
          fr: 'Nauru',
          jp: 'ナウル',
          it: 'Nauru',
        },
        callingCodes: ['674'],
      },
      {
        name: 'Nepal',
        alpha2Code: 'NP',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Népal',
          es: 'Nepal',
          fr: 'Népal',
          jp: 'ネパール',
          it: 'Nepal',
        },
        callingCodes: ['977'],
      },
      {
        name: 'Netherlands',
        alpha2Code: 'NL',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Niederlande',
          es: 'Países Bajos',
          fr: 'Pays-Bas',
          jp: 'オランダ',
          it: 'Paesi Bassi',
        },
        callingCodes: ['31'],
      },
      {
        name: 'New Caledonia',
        alpha2Code: 'NC',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Neukaledonien',
          es: 'Nueva Caledonia',
          fr: 'Nouvelle-Calédonie',
          jp: 'ニューカレドニア',
          it: 'Nuova Caledonia',
        },
        callingCodes: ['687'],
      },
      {
        name: 'New Zealand',
        alpha2Code: 'NZ',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Neuseeland',
          es: 'Nueva Zelanda',
          fr: 'Nouvelle-Zélande',
          jp: 'ニュージーランド',
          it: 'Nuova Zelanda',
        },
        callingCodes: ['64'],
      },
      {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Nicaragua',
          es: 'Nicaragua',
          fr: 'Nicaragua',
          jp: 'ニカラグア',
          it: 'Nicaragua',
        },
        callingCodes: ['505'],
      },
      {
        name: 'Niger',
        alpha2Code: 'NE',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Niger',
          es: 'Níger',
          fr: 'Niger',
          jp: 'ニジェール',
          it: 'Niger',
        },
        callingCodes: ['227'],
      },
      {
        name: 'Nigeria',
        alpha2Code: 'NG',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Nigeria',
          es: 'Nigeria',
          fr: 'Nigéria',
          jp: 'ナイジェリア',
          it: 'Nigeria',
        },
        callingCodes: ['234'],
      },
      {
        name: 'Niue',
        alpha2Code: 'NU',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Niue',
          es: 'Niue',
          fr: 'Niue',
          jp: 'ニウエ',
          it: 'Niue',
        },
        callingCodes: ['683'],
      },
      {
        name: 'Norfolk Island',
        alpha2Code: 'NF',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Norfolkinsel',
          es: 'Isla de Norfolk',
          fr: 'Île de Norfolk',
          jp: 'ノーフォーク島',
          it: 'Isola Norfolk',
        },
        callingCodes: ['672'],
      },
      {
        name: 'North Korea',
        alpha2Code: 'KP',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Nordkorea',
          es: 'Corea del Norte',
          fr: 'Corée du Nord',
          jp: '朝鮮民主主義人民共和国',
          it: 'Corea del Nord',
        },
        callingCodes: ['850'],
      },
      {
        name: 'Northern Mariana Islands',
        alpha2Code: 'MP',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Nördliche Marianen',
          es: 'Islas Marianas del Norte',
          fr: 'Îles Mariannes du Nord',
          jp: '北マリアナ諸島',
          it: 'Isole Marianne Settentrionali',
        },
        callingCodes: ['1670'],
      },
      {
        name: 'Norway',
        alpha2Code: 'NO',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Norwegen',
          es: 'Noruega',
          fr: 'Norvège',
          jp: 'ノルウェー',
          it: 'Norvegia',
        },
        callingCodes: ['47'],
      },
      {
        name: 'Oman',
        alpha2Code: 'OM',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Oman',
          es: 'Omán',
          fr: 'Oman',
          jp: 'オマーン',
          it: 'oman',
        },
        callingCodes: ['968'],
      },
      {
        name: 'Pakistan',
        alpha2Code: 'PK',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Pakistan',
          es: 'Pakistán',
          fr: 'Pakistan',
          jp: 'パキスタン',
          it: 'Pakistan',
        },
        callingCodes: ['92'],
      },
      {
        name: 'Palau',
        alpha2Code: 'PW',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Palau',
          es: 'Palau',
          fr: 'Palaos',
          jp: 'パラオ',
          it: 'Palau',
        },
        callingCodes: ['680'],
      },
      {
        name: 'Palestine',
        alpha2Code: 'PS',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Palästina',
          es: 'Palestina',
          fr: 'Palestine',
          jp: 'パレスチナ',
          it: 'Palestina',
        },
        callingCodes: ['970'],
      },
      {
        name: 'Panama',
        alpha2Code: 'PA',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Panama',
          es: 'Panamá',
          fr: 'Panama',
          jp: 'パナマ',
          it: 'Panama',
        },
        callingCodes: ['507'],
      },
      {
        name: 'Papua New Guinea',
        alpha2Code: 'PG',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Papua-Neuguinea',
          es: 'Papúa Nueva Guinea',
          fr: 'Papouasie-Nouvelle-Guinée',
          jp: 'パプアニューギニア',
          it: 'Papua Nuova Guinea',
        },
        callingCodes: ['675'],
      },
      {
        name: 'Paraguay',
        alpha2Code: 'PY',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Paraguay',
          es: 'Paraguay',
          fr: 'Paraguay',
          jp: 'パラグアイ',
          it: 'Paraguay',
        },
        callingCodes: ['595'],
      },
      {
        name: 'Peru',
        alpha2Code: 'PE',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Peru',
          es: 'Perú',
          fr: 'Pérou',
          jp: 'ペルー',
          it: 'Perù',
        },
        callingCodes: ['51'],
      },
      {
        name: 'Philippines',
        alpha2Code: 'PH',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Philippinen',
          es: 'Filipinas',
          fr: 'Philippines',
          jp: 'フィリピン',
          it: 'Filippine',
        },
        callingCodes: ['63'],
      },
      {
        name: 'Pitcairn Islands',
        alpha2Code: 'PN',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Pitcairn',
          es: 'Islas Pitcairn',
          fr: 'Îles Pitcairn',
          jp: 'ピトケアン',
          it: 'Isole Pitcairn',
        },
        callingCodes: ['64'],
      },
      {
        name: 'Poland',
        alpha2Code: 'PL',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Polen',
          es: 'Polonia',
          fr: 'Pologne',
          jp: 'ポーランド',
          it: 'Polonia',
        },
        callingCodes: ['48'],
      },
      {
        name: 'Portugal',
        alpha2Code: 'PT',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Portugal',
          es: 'Portugal',
          fr: 'Portugal',
          jp: 'ポルトガル',
          it: 'Portogallo',
        },
        callingCodes: ['351'],
      },
      {
        name: 'Puerto Rico',
        alpha2Code: 'PR',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Puerto Rico',
          es: 'Puerto Rico',
          fr: 'Porto Rico',
          jp: 'プエルトリコ',
          it: 'Porto Rico',
        },
        callingCodes: ['1787', '1939'],
      },
      {
        name: 'Qatar',
        alpha2Code: 'QA',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Katar',
          es: 'Catar',
          fr: 'Qatar',
          jp: 'カタール',
          it: 'Qatar',
        },
        callingCodes: ['974'],
      },
      {
        name: 'Republic of Kosovo',
        alpha2Code: 'XK',
        region: ECountryRegion.EUROPE,
        translations: {
          de: null,
          es: 'Kosovo',
          fr: null,
          jp: null,
          it: null,
        },
        callingCodes: ['383'],
      },
      {
        name: 'Réunion',
        alpha2Code: 'RE',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Réunion',
          es: 'Reunión',
          fr: 'Réunion',
          jp: 'レユニオン',
          it: 'Riunione',
        },
        callingCodes: ['262'],
      },
      {
        name: 'Romania',
        alpha2Code: 'RO',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Rumänien',
          es: 'Rumania',
          fr: 'Roumanie',
          jp: 'ルーマニア',
          it: 'Romania',
        },
        callingCodes: ['40'],
      },
      {
        name: 'Russia',
        alpha2Code: 'RU',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Russland',
          es: 'Rusia',
          fr: 'Russie',
          jp: 'ロシア連邦',
          it: 'Russia',
        },
        callingCodes: ['7'],
      },
      {
        name: 'Rwanda',
        alpha2Code: 'RW',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Ruanda',
          es: 'Ruanda',
          fr: 'Rwanda',
          jp: 'ルワンダ',
          it: 'Ruanda',
        },
        callingCodes: ['250'],
      },
      {
        name: 'Saint Barthélemy',
        alpha2Code: 'BL',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Saint-Barthélemy',
          es: 'San Bartolomé',
          fr: 'Saint-Barthélemy',
          jp: 'サン・バルテルミー',
          it: 'Antille Francesi',
        },
        callingCodes: ['590'],
      },
      {
        name: 'Saint Helena',
        alpha2Code: 'SH',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Sankt Helena',
          es: 'Santa Helena',
          fr: 'Sainte-Hélène',
          jp: 'セントヘレナ・アセンションおよびトリスタンダクーニャ',
          it: "Sant'Elena",
        },
        callingCodes: ['290'],
      },
      {
        name: 'Saint Kitts and Nevis',
        alpha2Code: 'KN',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'St. Kitts und Nevis',
          es: 'San Cristóbal y Nieves',
          fr: 'Saint-Christophe-et-Niévès',
          jp: 'セントクリストファー・ネイビス',
          it: 'Saint Kitts e Nevis',
        },
        callingCodes: ['1869'],
      },
      {
        name: 'Saint Lucia',
        alpha2Code: 'LC',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Saint Lucia',
          es: 'Santa Lucía',
          fr: 'Saint-Lucie',
          jp: 'セントルシア',
          it: 'Santa Lucia',
        },
        callingCodes: ['1758'],
      },
      {
        name: 'Saint Martin',
        alpha2Code: 'MF',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Saint Martin',
          es: 'Saint Martin',
          fr: 'Saint-Martin',
          jp: 'サン・マルタン（フランス領）',
          it: 'Saint Martin',
        },
        callingCodes: ['590'],
      },
      {
        name: 'Saint Pierre and Miquelon',
        alpha2Code: 'PM',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Saint-Pierre und Miquelon',
          es: 'San Pedro y Miquelón',
          fr: 'Saint-Pierre-et-Miquelon',
          jp: 'サンピエール島・ミクロン島',
          it: 'Saint-Pierre e Miquelon',
        },
        callingCodes: ['508'],
      },
      {
        name: 'Saint Vincent and the Grenadines',
        alpha2Code: 'VC',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Saint Vincent und die Grenadinen',
          es: 'San Vicente y Granadinas',
          fr: 'Saint-Vincent-et-les-Grenadines',
          jp: 'セントビンセントおよびグレナディーン諸島',
          it: 'Saint Vincent e Grenadine',
        },
        callingCodes: ['1784'],
      },
      {
        name: 'Samoa',
        alpha2Code: 'WS',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Samoa',
          es: 'Samoa',
          fr: 'Samoa',
          jp: 'サモア',
          it: 'Samoa',
        },
        callingCodes: ['685'],
      },
      {
        name: 'San Marino',
        alpha2Code: 'SM',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'San Marino',
          es: 'San Marino',
          fr: 'Saint-Marin',
          jp: 'サンマリノ',
          it: 'San Marino',
        },
        callingCodes: ['378'],
      },
      {
        name: 'São Tomé and Príncipe',
        alpha2Code: 'ST',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'São Tomé und Príncipe',
          es: 'Santo Tomé y Príncipe',
          fr: 'Sao Tomé-et-Principe',
          jp: 'サントメ・プリンシペ',
          it: 'São Tomé e Príncipe',
        },
        callingCodes: ['239'],
      },
      {
        name: 'Saudi Arabia',
        alpha2Code: 'SA',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Saudi-Arabien',
          es: 'Arabia Saudí',
          fr: 'Arabie Saoudite',
          jp: 'サウジアラビア',
          it: 'Arabia Saudita',
        },
        callingCodes: ['966'],
      },
      {
        name: 'Senegal',
        alpha2Code: 'SN',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Senegal',
          es: 'Senegal',
          fr: 'Sénégal',
          jp: 'セネガル',
          it: 'Senegal',
        },
        callingCodes: ['221'],
      },
      {
        name: 'Serbia',
        alpha2Code: 'RS',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Serbien',
          es: 'Serbia',
          fr: 'Serbie',
          jp: 'セルビア',
          it: 'Serbia',
        },
        callingCodes: ['381'],
      },
      {
        name: 'Seychelles',
        alpha2Code: 'SC',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Seychellen',
          es: 'Seychelles',
          fr: 'Seychelles',
          jp: 'セーシェル',
          it: 'Seychelles',
        },
        callingCodes: ['248'],
      },
      {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Sierra Leone',
          es: 'Sierra Leone',
          fr: 'Sierra Leone',
          jp: 'シエラレオネ',
          it: 'Sierra Leone',
        },
        callingCodes: ['232'],
      },
      {
        name: 'Singapore',
        alpha2Code: 'SG',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Singapur',
          es: 'Singapur',
          fr: 'Singapour',
          jp: 'シンガポール',
          it: 'Singapore',
        },
        callingCodes: ['65'],
      },
      {
        name: 'Sint Maarten',
        alpha2Code: 'SX',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: null,
          es: null,
          fr: null,
          jp: null,
          it: null,
        },
        callingCodes: ['1721'],
      },
      {
        name: 'Slovakia',
        alpha2Code: 'SK',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Slowakei',
          es: 'República Eslovaca',
          fr: 'Slovaquie',
          jp: 'スロバキア',
          it: 'Slovacchia',
        },
        callingCodes: ['421'],
      },
      {
        name: 'Slovenia',
        alpha2Code: 'SI',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Slowenien',
          es: 'Eslovenia',
          fr: 'Slovénie',
          jp: 'スロベニア',
          it: 'Slovenia',
        },
        callingCodes: ['386'],
      },
      {
        name: 'Solomon Islands',
        alpha2Code: 'SB',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Salomonen',
          es: 'Islas Salomón',
          fr: 'Îles Salomon',
          jp: 'ソロモン諸島',
          it: 'Isole Salomone',
        },
        callingCodes: ['677'],
      },
      {
        name: 'Somalia',
        alpha2Code: 'SO',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Somalia',
          es: 'Somalia',
          fr: 'Somalie',
          jp: 'ソマリア',
          it: 'Somalia',
        },
        callingCodes: ['252'],
      },
      {
        name: 'South Africa',
        alpha2Code: 'ZA',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Republik Südafrika',
          es: 'República de Sudáfrica',
          fr: 'Afrique du Sud',
          jp: '南アフリカ',
          it: 'Sud Africa',
        },
        callingCodes: ['27'],
      },
      {
        name: 'South Georgia',
        alpha2Code: 'GS',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Südgeorgien und die Südlichen Sandwichinseln',
          es: 'Islas Georgias del Sur y Sandwich del Sur',
          fr: 'Géorgie du Sud-et-les Îles Sandwich du Sud',
          jp: 'サウスジョージア・サウスサンドウィッチ諸島',
          it: 'Georgia del Sud e Isole Sandwich Meridionali',
        },
        callingCodes: ['500'],
      },
      {
        name: 'South Korea',
        alpha2Code: 'KR',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Südkorea',
          es: 'Corea del Sur',
          fr: 'Corée du Sud',
          jp: '大韓民国',
          it: 'Corea del Sud',
        },
        callingCodes: ['82'],
      },
      {
        name: 'South Sudan',
        alpha2Code: 'SS',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Südsudan',
          es: 'Sudán del Sur',
          fr: 'Soudan du Sud',
          jp: '南スーダン',
          it: 'Sudan del sud',
        },
        callingCodes: ['211'],
      },
      {
        name: 'Spain',
        alpha2Code: 'ES',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Spanien',
          es: 'España',
          fr: 'Espagne',
          jp: 'スペイン',
          it: 'Spagna',
        },
        callingCodes: ['34'],
      },
      {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Sri Lanka',
          es: 'Sri Lanka',
          fr: 'Sri Lanka',
          jp: 'スリランカ',
          it: 'Sri Lanka',
        },
        callingCodes: ['94'],
      },
      {
        name: 'Sudan',
        alpha2Code: 'SD',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Sudan',
          es: 'Sudán',
          fr: 'Soudan',
          jp: 'スーダン',
          it: 'Sudan',
        },
        callingCodes: ['249'],
      },
      {
        name: 'Suriname',
        alpha2Code: 'SR',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Suriname',
          es: 'Surinam',
          fr: 'Surinam',
          jp: 'スリナム',
          it: 'Suriname',
        },
        callingCodes: ['597'],
      },
      {
        name: 'Svalbard and Jan Mayen',
        alpha2Code: 'SJ',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Svalbard und Jan Mayen',
          es: 'Islas Svalbard y Jan Mayen',
          fr: 'Svalbard et Jan Mayen',
          jp: 'スヴァールバル諸島およびヤンマイエン島',
          it: 'Svalbard e Jan Mayen',
        },
        callingCodes: ['4779'],
      },
      {
        name: 'Swaziland',
        alpha2Code: 'SZ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Swasiland',
          es: 'Suazilandia',
          fr: 'Swaziland',
          jp: 'スワジランド',
          it: 'Swaziland',
        },
        callingCodes: ['268'],
      },
      {
        name: 'Sweden',
        alpha2Code: 'SE',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Schweden',
          es: 'Suecia',
          fr: 'Suède',
          jp: 'スウェーデン',
          it: 'Svezia',
        },
        callingCodes: ['46'],
      },
      {
        name: 'Switzerland',
        alpha2Code: 'CH',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Schweiz',
          es: 'Suiza',
          fr: 'Suisse',
          jp: 'スイス',
          it: 'Svizzera',
        },
        callingCodes: ['41'],
      },
      {
        name: 'Syria',
        alpha2Code: 'SY',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Syrien',
          es: 'Siria',
          fr: 'Syrie',
          jp: 'シリア・アラブ共和国',
          it: 'Siria',
        },
        callingCodes: ['963'],
      },
      {
        name: 'Taiwan',
        alpha2Code: 'TW',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Taiwan',
          es: 'Taiwán',
          fr: 'Taïwan',
          jp: '台湾（台湾省/中華民国）',
          it: 'Taiwan',
        },
        callingCodes: ['886'],
      },
      {
        name: 'Tajikistan',
        alpha2Code: 'TJ',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Tadschikistan',
          es: 'Tayikistán',
          fr: 'Tadjikistan',
          jp: 'タジキスタン',
          it: 'Tagikistan',
        },
        callingCodes: ['992'],
      },
      {
        name: 'Tanzania',
        alpha2Code: 'TZ',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Tansania',
          es: 'Tanzania',
          fr: 'Tanzanie',
          jp: 'タンザニア',
          it: 'Tanzania',
        },
        callingCodes: ['255'],
      },
      {
        name: 'Thailand',
        alpha2Code: 'TH',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Thailand',
          es: 'Tailandia',
          fr: 'Thaïlande',
          jp: 'タイ',
          it: 'Tailandia',
        },
        callingCodes: ['66'],
      },
      {
        name: 'East Timor',
        alpha2Code: 'TL',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Timor-Leste',
          es: 'Timor Oriental',
          fr: 'Timor oriental',
          jp: '東ティモール',
          it: 'Timor Est',
        },
        callingCodes: ['670'],
      },
      {
        name: 'Togo',
        alpha2Code: 'TG',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Togo',
          es: 'Togo',
          fr: 'Togo',
          jp: 'トーゴ',
          it: 'Togo',
        },
        callingCodes: ['228'],
      },
      {
        name: 'Tokelau',
        alpha2Code: 'TK',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Tokelau',
          es: 'Islas Tokelau',
          fr: 'Tokelau',
          jp: 'トケラウ',
          it: 'Isole Tokelau',
        },
        callingCodes: ['690'],
      },
      {
        name: 'Tonga',
        alpha2Code: 'TO',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Tonga',
          es: 'Tonga',
          fr: 'Tonga',
          jp: 'トンガ',
          it: 'Tonga',
        },
        callingCodes: ['676'],
      },
      {
        name: 'Trinidad and Tobago',
        alpha2Code: 'TT',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Trinidad und Tobago',
          es: 'Trinidad y Tobago',
          fr: 'Trinité et Tobago',
          jp: 'トリニダード・トバゴ',
          it: 'Trinidad e Tobago',
        },
        callingCodes: ['1868'],
      },
      {
        name: 'Tunisia',
        alpha2Code: 'TN',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Tunesien',
          es: 'Túnez',
          fr: 'Tunisie',
          jp: 'チュニジア',
          it: 'Tunisia',
        },
        callingCodes: ['216'],
      },
      {
        name: 'Turkey',
        alpha2Code: 'TR',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Türkei',
          es: 'Turquía',
          fr: 'Turquie',
          jp: 'トルコ',
          it: 'Turchia',
        },
        callingCodes: ['90'],
      },
      {
        name: 'Turkmenistan',
        alpha2Code: 'TM',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Turkmenistan',
          es: 'Turkmenistán',
          fr: 'Turkménistan',
          jp: 'トルクメニスタン',
          it: 'Turkmenistan',
        },
        callingCodes: ['993'],
      },
      {
        name: 'Turks and Caicos Islands',
        alpha2Code: 'TC',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Turks- und Caicosinseln',
          es: 'Islas Turks y Caicos',
          fr: 'Îles Turques-et-Caïques',
          jp: 'タークス・カイコス諸島',
          it: 'Isole Turks e Caicos',
        },
        callingCodes: ['1649'],
      },
      {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Tuvalu',
          es: 'Tuvalu',
          fr: 'Tuvalu',
          jp: 'ツバル',
          it: 'Tuvalu',
        },
        callingCodes: ['688'],
      },
      {
        name: 'Uganda',
        alpha2Code: 'UG',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Uganda',
          es: 'Uganda',
          fr: 'Uganda',
          jp: 'ウガンダ',
          it: 'Uganda',
        },
        callingCodes: ['256'],
      },
      {
        name: 'Ukraine',
        alpha2Code: 'UA',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Ukraine',
          es: 'Ucrania',
          fr: 'Ukraine',
          jp: 'ウクライナ',
          it: 'Ucraina',
        },
        callingCodes: ['380'],
      },
      {
        name: 'United Arab Emirates',
        alpha2Code: 'AE',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Vereinigte Arabische Emirate',
          es: 'Emiratos Árabes Unidos',
          fr: 'Émirats arabes unis',
          jp: 'アラブ首長国連邦',
          it: 'Emirati Arabi Uniti',
        },
        callingCodes: ['971'],
      },
      {
        name: 'United Kingdom',
        alpha2Code: 'GB',
        region: ECountryRegion.EUROPE,
        translations: {
          de: 'Vereinigtes Königreich',
          es: 'Reino Unido',
          fr: 'Royaume-Uni',
          jp: 'イギリス',
          it: 'Regno Unito',
        },
        callingCodes: ['44'],
      },
      {
        name: 'United States',
        alpha2Code: 'US',
        region: ECountryRegion.NORTH_AMERICA,
        translations: {
          de: 'Vereinigte Staaten von Amerika',
          es: 'Estados Unidos',
          fr: 'États-Unis',
          jp: 'アメリカ合衆国',
          it: "Stati Uniti D'America",
        },
        callingCodes: ['1'],
      },
      {
        name: 'Uruguay',
        alpha2Code: 'UY',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Uruguay',
          es: 'Uruguay',
          fr: 'Uruguay',
          jp: 'ウルグアイ',
          it: 'Uruguay',
        },
        callingCodes: ['598'],
      },
      {
        name: 'Uzbekistan',
        alpha2Code: 'UZ',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Usbekistan',
          es: 'Uzbekistán',
          fr: 'Ouzbékistan',
          jp: 'ウズベキスタン',
          it: 'Uzbekistan',
        },
        callingCodes: ['998'],
      },
      {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Vanuatu',
          es: 'Vanuatu',
          fr: 'Vanuatu',
          jp: 'バヌアツ',
          it: 'Vanuatu',
        },
        callingCodes: ['678'],
      },
      {
        name: 'Venezuela',
        alpha2Code: 'VE',
        region: ECountryRegion.SOUTH_AMERICA,
        translations: {
          de: 'Venezuela',
          es: 'Venezuela',
          fr: 'Venezuela',
          jp: 'ベネズエラ・ボリバル共和国',
          it: 'Venezuela',
        },
        callingCodes: ['58'],
      },
      {
        name: 'Vietnam',
        alpha2Code: 'VN',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Vietnam',
          es: 'Vietnam',
          fr: 'Viêt Nam',
          jp: 'ベトナム',
          it: 'Vietnam',
        },
        callingCodes: ['84'],
      },
      {
        name: 'Wallis and Futuna',
        alpha2Code: 'WF',
        region: ECountryRegion.AUSTRALIA_PACIFIC,
        translations: {
          de: 'Wallis und Futuna',
          es: 'Wallis y Futuna',
          fr: 'Wallis-et-Futuna',
          jp: 'ウォリス・フツナ',
          it: 'Wallis e Futuna',
        },
        callingCodes: ['681'],
      },
      {
        name: 'Western Sahara',
        alpha2Code: 'EH',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Westsahara',
          es: 'Sahara Occidental',
          fr: 'Sahara Occidental',
          jp: '西サハラ',
          it: 'Sahara Occidentale',
        },
        callingCodes: ['212'],
      },
      {
        name: 'Yemen',
        alpha2Code: 'YE',
        region: ECountryRegion.ASIA,
        translations: {
          de: 'Jemen',
          es: 'Yemen',
          fr: 'Yémen',
          jp: 'イエメン',
          it: 'Yemen',
        },
        callingCodes: ['967'],
      },
      {
        name: 'Zambia',
        alpha2Code: 'ZM',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Sambia',
          es: 'Zambia',
          fr: 'Zambie',
          jp: 'ザンビア',
          it: 'Zambia',
        },
        callingCodes: ['260'],
      },
      {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        region: ECountryRegion.AFRICA,
        translations: {
          de: 'Simbabwe',
          es: 'Zimbabue',
          fr: 'Zimbabwe',
          jp: 'ジンバブエ',
          it: 'Zimbabwe',
        },
        callingCodes: ['263'],
      },
    ];
  }
}
